import { useEffect, useId, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";
import {
  moveSectionInOrder,
  patchRcaMeasurementsConfig,
  RCA_MEASUREMENT_SECTION_LABELS,
} from "@/components/root-cause-analysis/rca-measurements-config-storage.js";
import RcaApplyButton, {
  useRcaApplyAction,
} from "@/components/root-cause-analysis/rca-apply-button.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import { Input } from "@/components/ui/input.jsx";
import { cn } from "@/lib/utils.js";

/**
 * Collapsible RCA measurements settings (alarm limits, key-trend score, z-score).
 *
 * @param {{
 *   config: import("@/components/root-cause-analysis/rca-measurements-config-storage.js").RcaMeasurementsConfig,
 *   onConfigChange: (config: import("@/components/root-cause-analysis/rca-measurements-config-storage.js").RcaMeasurementsConfig) => void,
 * }} props
 */
export default function RcaMeasurementsConfigure({ config, onConfigChange }) {
  const [keyTrendScoreDraft, setKeyTrendScoreDraft] = useState(
    () => config.keyTrendScoreThreshold
  );
  const [zScoreDraft, setZScoreDraft] = useState(() => config.zScoreThreshold);
  const [configureOpen, setConfigureOpen] = useState(false);
  const configureToggleId = useId();
  const configurePanelId = useId();
  const keyTrendScoreInputId = useId();
  const zScoreInputId = useId();

  useEffect(() => {
    setKeyTrendScoreDraft(config.keyTrendScoreThreshold);
  }, [config.keyTrendScoreThreshold]);

  useEffect(() => {
    setZScoreDraft(config.zScoreThreshold);
  }, [config.zScoreThreshold]);

  function handleUseAlarmLimitsChange(checked) {
    onConfigChange(patchRcaMeasurementsConfig({ useAlarmLimits: checked }));
  }

  const keyTrendApply = useRcaApplyAction(() => {
    if (!Number.isFinite(keyTrendScoreDraft) || keyTrendScoreDraft < 0) {
      return false;
    }
    onConfigChange(
      patchRcaMeasurementsConfig({
        keyTrendScoreThreshold: keyTrendScoreDraft,
      })
    );
  });

  const zScoreApply = useRcaApplyAction(() => {
    if (!Number.isFinite(zScoreDraft) || zScoreDraft < 0) return false;
    onConfigChange(
      patchRcaMeasurementsConfig({ zScoreThreshold: zScoreDraft })
    );
  });

  return (
    <div className='mt-3 border-t border-border pt-2'>
      <button
        type='button'
        id={configureToggleId}
        className='flex w-full max-w-md items-center gap-1.5 rounded-md py-1 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
        onClick={() => setConfigureOpen((v) => !v)}
        aria-expanded={configureOpen}
        aria-controls={configurePanelId}>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            configureOpen && "rotate-180"
          )}
          aria-hidden
        />
        Configure
      </button>
      {configureOpen ? (
        <div
          id={configurePanelId}
          role='region'
          aria-labelledby={configureToggleId}
          className='mt-2 max-w-md space-y-2 pl-5 text-[11px] leading-snug text-muted-foreground'>
          <label className='flex cursor-pointer items-center gap-2'>
            <Checkbox
              checked={config.useAlarmLimits}
              onChange={(e) => handleUseAlarmLimitsChange(e.target.checked)}
            />
            <span>Use Alarm Limits</span>
          </label>
          {config.useAlarmLimits ? (
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1.5'>
              <label
                htmlFor={keyTrendScoreInputId}
                className='flex items-center gap-1.5 whitespace-nowrap'>
                <span>Key trend score</span>
                <Input
                  id={keyTrendScoreInputId}
                  type='number'
                  inputMode='numeric'
                  min={0}
                  max={100}
                  step={1}
                  value={
                    Number.isFinite(keyTrendScoreDraft) ? keyTrendScoreDraft : ""
                  }
                  onChange={(e) => {
                    const el = e.target;
                    if (el.value === "") {
                      setKeyTrendScoreDraft(NaN);
                      return;
                    }
                    const n = el.valueAsNumber;
                    setKeyTrendScoreDraft(Number.isFinite(n) ? n : NaN);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      keyTrendApply.applyAndFlash();
                    }
                  }}
                  className='h-8 w-[4.5rem] px-2 py-1 text-xs tabular-nums'
                />
              </label>
              <RcaApplyButton
                showSuccess={keyTrendApply.showSuccess}
                onClick={keyTrendApply.applyAndFlash}
              />
            </div>
          ) : (
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1.5'>
              <label
                htmlFor={zScoreInputId}
                className='flex items-center gap-1.5 whitespace-nowrap'>
                <span>Z-score threshold</span>
                <Input
                  id={zScoreInputId}
                  type='number'
                  inputMode='decimal'
                  min={0}
                  step={0.1}
                  value={Number.isFinite(zScoreDraft) ? zScoreDraft : ""}
                  onChange={(e) => {
                    const el = e.target;
                    if (el.value === "") {
                      setZScoreDraft(NaN);
                      return;
                    }
                    const n = el.valueAsNumber;
                    setZScoreDraft(Number.isFinite(n) ? n : NaN);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      zScoreApply.applyAndFlash();
                    }
                  }}
                  className='h-8 w-[4.5rem] px-2 py-1 text-xs tabular-nums'
                />
              </label>
              <RcaApplyButton
                showSuccess={zScoreApply.showSuccess}
                onClick={zScoreApply.applyAndFlash}
              />
            </div>
          )}

          <div className='border-t border-border/80 pt-2'>
            <p className='mb-1.5 text-[11px] font-medium text-foreground'>
              Section layout
            </p>
            <ul className='space-y-1.5'>
              {config.sectionOrder.map((sectionId, index) => (
                <li
                  key={sectionId}
                  className='flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border/80 bg-card/60 px-2 py-1.5'>
                  <span className='min-w-0 flex-1 text-[11px] font-medium text-foreground'>
                    {RCA_MEASUREMENT_SECTION_LABELS[sectionId]}
                  </span>
                  <div className='flex shrink-0 items-center gap-1'>
                    <button
                      type='button'
                      className='inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-40'
                      disabled={index === 0}
                      aria-label={`Move ${RCA_MEASUREMENT_SECTION_LABELS[sectionId]} up`}
                      onClick={() =>
                        onConfigChange(
                          patchRcaMeasurementsConfig({
                            sectionOrder: moveSectionInOrder(
                              config.sectionOrder,
                              sectionId,
                              "up"
                            ),
                          })
                        )
                      }>
                      <ArrowUp className='h-3.5 w-3.5' aria-hidden />
                    </button>
                    <button
                      type='button'
                      className='inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-40'
                      disabled={index === config.sectionOrder.length - 1}
                      aria-label={`Move ${RCA_MEASUREMENT_SECTION_LABELS[sectionId]} down`}
                      onClick={() =>
                        onConfigChange(
                          patchRcaMeasurementsConfig({
                            sectionOrder: moveSectionInOrder(
                              config.sectionOrder,
                              sectionId,
                              "down"
                            ),
                          })
                        )
                      }>
                      <ArrowDown className='h-3.5 w-3.5' aria-hidden />
                    </button>
                  </div>
                  <label className='flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap'>
                    <Checkbox
                      checked={config.sectionExpanded[sectionId]}
                      onChange={(e) =>
                        onConfigChange(
                          patchRcaMeasurementsConfig({
                            sectionExpanded: {
                              [sectionId]: e.target.checked,
                            },
                          })
                        )
                      }
                    />
                    <span>Expanded</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
